<template>
  <div
    class="rich-text-editor"
    :class="{
      'is-focused': isFocused,
      'is-disabled': disabled,
      'has-error': hasError,
    }"
    :style="editorStyle"
  >
    <div v-if="editor" class="rich-text-toolbar">
      <b-button
        size="is-small"
        native-type="button"
        :disabled="disabled"
        :type="isActive('paragraph') ? 'is-primary' : 'is-light'"
        title="Párrafo normal"
        @click="editor.chain().focus().setParagraph().run()"
      >
        P
      </b-button>

      <b-button
        size="is-small"
        native-type="button"
        :disabled="disabled"
        :type="isActive('bold') ? 'is-primary' : 'is-light'"
        title="Negrita"
        @click="editor.chain().focus().toggleBold().run()"
      >
        <strong>B</strong>
      </b-button>

      <b-button
        size="is-small"
        native-type="button"
        :disabled="disabled"
        :type="isActive('italic') ? 'is-primary' : 'is-light'"
        title="Cursiva"
        @click="editor.chain().focus().toggleItalic().run()"
      >
        <em>I</em>
      </b-button>

      <b-button
        size="is-small"
        native-type="button"
        :disabled="disabled"
        :type="isActive('link') ? 'is-primary' : 'is-light'"
        title="Agregar o editar enlace"
        @click="setLink"
      >
        🔗
      </b-button>

      <b-button
        size="is-small"
        native-type="button"
        :disabled="disabled || !isActive('link')"
        type="is-light"
        title="Quitar enlace"
        @click="editor.chain().focus().unsetLink().run()"
      >
        ×🔗
      </b-button>

      <b-button
        size="is-small"
        native-type="button"
        :disabled="disabled"
        :type="isActive('strike') ? 'is-primary' : 'is-light'"
        title="Tachado"
        @click="editor.chain().focus().toggleStrike().run()"
      >
        <s>S</s>
      </b-button>

      <span class="editor-divider"></span>

      <b-button
        size="is-small"
        native-type="button"
        :disabled="disabled"
        :type="isActive('heading', { level: 2 }) ? 'is-primary' : 'is-light'"
        title="Título H2"
        @click="editor.chain().focus().toggleHeading({ level: 2 }).run()"
      >
        H2
      </b-button>

      <b-button
        size="is-small"
        native-type="button"
        :disabled="disabled"
        :type="isActive('heading', { level: 3 }) ? 'is-primary' : 'is-light'"
        title="Título H3"
        @click="editor.chain().focus().toggleHeading({ level: 3 }).run()"
      >
        H3
      </b-button>

      <span class="editor-divider"></span>

      <b-button
        size="is-small"
        native-type="button"
        :disabled="disabled"
        :type="isActive('bulletList') ? 'is-primary' : 'is-light'"
        title="Lista con viñetas"
        @click="editor.chain().focus().toggleBulletList().run()"
      >
        •
      </b-button>

      <b-button
        size="is-small"
        native-type="button"
        :disabled="disabled"
        :type="isActive('orderedList') ? 'is-primary' : 'is-light'"
        title="Lista numerada"
        @click="editor.chain().focus().toggleOrderedList().run()"
      >
        1.
      </b-button>

      <b-button
        size="is-small"
        native-type="button"
        :disabled="disabled"
        :type="isActive('blockquote') ? 'is-primary' : 'is-light'"
        title="Cita"
        @click="editor.chain().focus().toggleBlockquote().run()"
      >
        ❝
      </b-button>

      <b-button
        size="is-small"
        native-type="button"
        :disabled="disabled"
        type="is-light"
        title="Separador horizontal"
        @click="editor.chain().focus().setHorizontalRule().run()"
      >
        —
      </b-button>

      <span class="editor-divider"></span>

      <b-button
        size="is-small"
        native-type="button"
        :disabled="disabled || !editor.can().chain().focus().undo().run()"
        type="is-light"
        title="Deshacer"
        @click="editor.chain().focus().undo().run()"
      >
        ↶
      </b-button>

      <b-button
        size="is-small"
        native-type="button"
        :disabled="disabled || !editor.can().chain().focus().redo().run()"
        type="is-light"
        title="Rehacer"
        @click="editor.chain().focus().redo().run()"
      >
        ↷
      </b-button>

      <b-button
        size="is-small"
        native-type="button"
        :disabled="disabled"
        type="is-light"
        title="Quitar formato"
        @click="clearFormatting"
      >
        Limpiar
      </b-button>
    </div>

    <div class="rich-text-body" @click="focusEditor">
      <EditorContent v-if="editor" :editor="editor" class="rich-text-content" />

      <span
        v-if="isEmpty && placeholder && !disabled"
        class="rich-text-placeholder"
      >
        {{ placeholder }}
      </span>
    </div>
  </div>
</template>

<script>
import { Editor, EditorContent } from "@tiptap/vue-2";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";

export default {
  name: "RichTextEditor",

  components: {
    EditorContent,
  },

  props: {
    value: {
      type: String,
      default: "",
    },

    placeholder: {
      type: String,
      default: "Escribe el contenido...",
    },

    minHeight: {
      type: Number,
      default: 200,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    hasError: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      editor: null,
      isEmpty: true,
      isFocused: false,
    };
  },

  computed: {
    editorStyle() {
      return {
        "--editor-min-height": `${this.minHeight}px`,
      };
    },
  },

  watch: {
    value(newValue) {
      if (!this.editor) {
        return;
      }

      const incomingContent = newValue || "";
      const currentContent = this.editor.isEmpty ? "" : this.editor.getHTML();

      if (currentContent === incomingContent) {
        return;
      }

      this.editor.commands.setContent(incomingContent, false);

      this.isEmpty = this.editor.isEmpty;
    },

    disabled(newValue) {
      if (this.editor) {
        this.editor.setEditable(!newValue);
      }
    },
  },

  mounted() {
    this.editor = new Editor({
      content: this.value || "",
      editable: !this.disabled,

      extensions: [
        StarterKit.configure({
          heading: {
            levels: [2, 3],
          },
        }),
        Link.configure({
          openOnClick: false,
          HTMLAttributes: {
            target: "_blank",
            rel: "noopener noreferrer",
          },
        }),
      ],

      editorProps: {
        attributes: {
          spellcheck: "true",
          autocomplete: "off",
          autocorrect: "on",
          autocapitalize: "sentences",
          "aria-label": this.placeholder,
        },
      },

      onCreate: ({ editor }) => {
        this.isEmpty = editor.isEmpty;
      },

      onUpdate: ({ editor }) => {
        this.isEmpty = editor.isEmpty;

        this.$emit("input", editor.isEmpty ? "" : editor.getHTML());
      },

      onFocus: () => {
        this.isFocused = true;
      },

      onBlur: () => {
        this.isFocused = false;
      },
    });
  },

  beforeDestroy() {
    if (this.editor) {
      this.editor.destroy();
    }
  },

  methods: {
    isActive(name, attributes = {}) {
      if (!this.editor) {
        return false;
      }

      return this.editor.isActive(name, attributes);
    },

    focusEditor() {
      if (!this.editor || this.disabled) {
        return;
      }

      this.editor.chain().focus().run();
    },

    clearFormatting() {
      if (!this.editor || this.disabled) {
        return;
      }

      this.editor.chain().focus().unsetAllMarks().clearNodes().run();
    },

    setLink() {
      if (!this.editor || this.disabled) {
        return;
      }

      const currentHref = this.editor.getAttributes("link").href || "";
      let href = window.prompt("Ingresá la URL del enlace", currentHref);

      if (href === null) {
        return;
      }

      href = href.trim();

      if (!href) {
        this.editor.chain().focus().extendMarkRange("link").unsetLink().run();
        return;
      }

      if (/^www\./i.test(href)) {
        href = `https://${href}`;
      }

      this.editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href })
        .run();
    },
  },
};
</script>

<style lang="scss" scoped>
.rich-text-editor {
  width: 100%;
  overflow: hidden;
  background: #ffffff;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &.is-focused {
    border-color: #0093bf;
    box-shadow: 0 0 0 1px #0093bf;
  }

  &.has-error {
    border-color: #f14668;
  }

  &.is-disabled {
    background: #f5f5f5;
    opacity: 0.75;
  }
}

.rich-text-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
  min-height: 48px;
  padding: 0.5rem 0.65rem;
  background: #f5f5f5;
  border-bottom: 1px solid #dbdbdb;
}

.editor-divider {
  flex: 0 0 1px;
  width: 1px;
  height: 24px;
  margin: 0 0.15rem;
  background: #d0d0d0;
}

.rich-text-body {
  position: relative;
  width: 100%;
  min-height: var(--editor-min-height);
  background: #ffffff;
  cursor: text;
}

.rich-text-content {
  display: block;
  width: 100%;
  min-height: var(--editor-min-height);
}

.rich-text-placeholder {
  position: absolute;
  top: 1rem;
  left: 1rem;
  color: #b5b5b5;
  pointer-events: none;
  user-select: none;
}

::v-deep .rich-text-toolbar .button {
  min-width: 32px;
  height: 30px;
  margin: 0;
  padding-right: 0.55rem;
  padding-left: 0.55rem;
  font-size: 0.8rem;
  box-shadow: none;
}

::v-deep .rich-text-content .ProseMirror {
  width: 100%;
  min-height: var(--editor-min-height);
  padding: 1rem;
  color: #363636;
  font-family: inherit;
  font-size: 1rem;
  line-height: 1.6;
  background: transparent;
  border: 0;
  outline: none;
  box-sizing: border-box;
  cursor: text;

  p {
    margin: 0 0 0.75rem;
  }

  p:last-child {
    margin-bottom: 0;
  }

  h2 {
    margin: 0.8rem 0 0.6rem;
    font-size: 1.5rem;
    font-weight: 700;
    line-height: 1.3;
  }

  h3 {
    margin: 0.8rem 0 0.6rem;
    font-size: 1.2rem;
    font-weight: 700;
    line-height: 1.3;
  }

  ul,
  ol {
    margin: 0.75rem 0;
    padding-left: 1.75rem;
  }

  ul {
    list-style: disc;
  }

  ol {
    list-style: decimal;
  }

  li {
    margin-bottom: 0.25rem;
  }

  blockquote {
    margin: 0.9rem 0;
    padding: 0.6rem 1rem;
    color: #5c5c5c;
    background: #f7f7f7;
    border-left: 4px solid #0093bf;
  }

  a {
    color: #00758f;
    font-weight: 600;
    text-decoration: underline;
  }

  hr {
    margin: 1.25rem 0;
    border: 0;
    border-top: 1px solid #dbdbdb;
  }
}

@media screen and (max-width: 768px) {
  .rich-text-toolbar {
    gap: 0.25rem;
    padding: 0.45rem;
  }

  ::v-deep .rich-text-toolbar .button {
    min-width: 30px;
    padding-right: 0.45rem;
    padding-left: 0.45rem;
  }

  ::v-deep .rich-text-content .ProseMirror {
    padding: 0.85rem;
  }

  .rich-text-placeholder {
    top: 0.85rem;
    left: 0.85rem;
  }
}
</style>
